








import googleMapsApi from '@/api/googleMapsApi';
import IconLocationFilled from '@/assets/svg/icons/icon-location-filled.svg?inline-simple';
import {Colours} from '@/typings/enums/Colours';
import {MarkerClusterer} from '@googlemaps/markerclusterer';
import _ from 'lodash';
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';

export type Marker = { lat: number, lng: number, title?: string };
export const defaultLatLng: google.maps.LatLngLiteral = {lat: 52.1326, lng: 5.2913};

@Component({
  name: 'GoogleMaps', components: {
    IconLocationFilled
  }
})
export default class GoogleMaps extends Vue {
  @Prop({required: true}) apiKey!: string;
  @Prop() mapId?: string;
  @Prop({
    default: () => {
      return defaultLatLng;
    }
  }) center!: { lat: number, lng: number };
  @Prop({default: 7}) zoom!: number;
  @Prop({
    default: () => {
      return [];
    }
  }) markers!: Array<Marker>;
  @Prop({default: 0}) circleRadius?: number;
  @Prop({default: 0}) fallbackRadius?: number;
  @Prop({default: false}) initDrawCircle!: boolean;
  @Prop({default: false}) drawMarker!: boolean;

  private infoWindow?: google.maps.InfoWindow;
  private map?: google.maps.Map;
  private circle?: google.maps.Circle;
  private centerPoint?: google.maps.marker.AdvancedMarkerElement;
  private mapMarkers: Array<google.maps.marker.AdvancedMarkerElement> = [];
  private markerCluster?: MarkerClusterer;
  private _cachedSvgPath?: string;

  private createMarkers: _.DebouncedFunc<() => void> = _.debounce(this._createMarkers.bind(this),
      500);

  private get svgPath(): string {
    if (!this._cachedSvgPath) {
      const element: Element | undefined = (this.$refs.svgIcon as Vue)?.$el;
      const path: SVGPathElement | null = element?.querySelector<SVGPathElement>('path') ?? null;
      this._cachedSvgPath = path?.getAttribute('d') ?? '';
    }
    return this._cachedSvgPath;
  }

  private createMarkerElement(color: string): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '35');
    svg.setAttribute('height', '35');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', this.svgPath as string);
    path.setAttribute('fill', color);
    svg.appendChild(path);
    return svg;
  }

  /**
   * Be sure to load the google api with your api key before using this function
   */
  public static getLocationDistanceInKm(fromPoint: google.maps.LatLngLiteral | { lng: number, lat: number }, toPoint: google.maps.LatLngLiteral | { lng: number, lat: number }): number {
    const fromLatLng = new google.maps.LatLng(fromPoint);
    const toLatLng = new google.maps.LatLng(toPoint);
    const meters: number = google.maps.geometry.spherical.computeDistanceBetween(fromLatLng, toLatLng);
    return Math.ceil(meters / 1000);
  }

  created() {
    googleMapsApi.load(this.apiKey).then(() => {
      const mapOptions: google.maps.MapOptions = {
        center: this.center,
        minZoom: 1,
        maxZoom: 10,
        zoom: this.zoom,
        disableDefaultUI: true,
        zoomControl: true
      };

      if (this.mapId) {
        mapOptions.mapId = this.mapId;
      }

      this.map = new google.maps.Map(this.$refs.map as HTMLElement, mapOptions);
      this.markerCluster = new MarkerClusterer({
        map: this.map,
        markers: [],
        renderer: {
          render({count, position}: { count: number, position: google.maps.LatLng }) {
            const div = document.createElement('div');
            div.style.cssText = [
              'width:50px', 'height:50px', 'border-radius:50%',
              'background:#e8b922', 'display:flex',
              'align-items:center', 'justify-content:center',
              'color:#000', 'font-weight:bold', 'font-size:11px',
              'font-family: Arial, sans-serif',
            ].join(';');
            div.textContent = String(count);
            return new google.maps.marker.AdvancedMarkerElement({position, content: div});
          }
        }
      });
      this.infoWindow = new google.maps.InfoWindow();

      if (this.initDrawCircle || this.drawMarker) {
        const content = this.drawMarker
            ? this.createMarkerElement(Colours.BLACK)
            : (() => {
              const dot = document.createElement('div');
              dot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:#000';
              return dot;
            })();
        this.centerPoint = new google.maps.marker.AdvancedMarkerElement({
          position: this.center,
          map: this.map,
          content
        });
      }

      if (this.initDrawCircle) {
        this.drawCircle(this.circleRadius as number);
      }
    });
  }

  mounted() {
    this.createMarkers();
  }

  public setActiveMarkerByName(name: string) {
    const marker: google.maps.marker.AdvancedMarkerElement | undefined = this.mapMarkers.find((marker) => {
      return marker.title === name;
    });

    if (marker) {
      google.maps.event.trigger(marker, 'gmp-click');
    }
  }

  @Watch('markers', {deep: true})
  onMarkersChange() {
    this.createMarkers();
  }

  private deleteMarkers() {
    this.markerCluster?.clearMarkers();
    this.mapMarkers.forEach(mapMarker => {
      mapMarker.map = null;
    });
    this.mapMarkers = [];
  }

  private _createMarkers() {
    googleMapsApi.load(this.apiKey).then(() => {
      this.deleteMarkers();

      // Get markers within radius
      let radiusRestrictedMarkers = this.filterByRadius(this.markers, (this.circleRadius as number));
      let noResultsInRange = false;
      if (radiusRestrictedMarkers.length === 0) {
        radiusRestrictedMarkers = this.filterByRadius(this.markers, (this.fallbackRadius as number));
        noResultsInRange = true;
      }

      const newMarkers = this.markers.map(location => this.createAdvancedMarker(location));
      this.mapMarkers = newMarkers;
      this.markerCluster?.addMarkers(newMarkers);
      this.$emit('markersInsideCircleUpdated', radiusRestrictedMarkers, noResultsInRange);
    });
  }

  private drawCircle(circleRadius: number): void {
    if (!this.circle) {
      this.circle = new google.maps.Circle({
        map: this.map,
        radius: circleRadius,
        strokeColor: Colours.BRAND,
        fillOpacity: 0,
        center: this.map?.getCenter(),
      });
    } else {
      this.circle.setRadius(circleRadius);
    }
    this.map?.fitBounds(this.circle.getBounds()!, 0);
  }

  private createAdvancedMarker(location: Marker): google.maps.marker.AdvancedMarkerElement {
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: location,
      map: this.map,
      title: location.title,
      content: this.createMarkerElement(Colours.BRAND),
    });
    marker.addListener('gmp-click', () => {
      this.$emit('markerClicked', marker);
      this.map?.panTo(marker.position as google.maps.LatLng | google.maps.LatLngLiteral);

      this.mapMarkers.forEach((selectedMarker) => {
        const path = (selectedMarker.content as SVGElement).querySelector('path');
        if (path) {
          path.setAttribute('fill', selectedMarker === marker ? Colours.BLACK : Colours.BRAND);
        }
      });
    });
    return marker;
  }

  private smoothZoom(map: google.maps.Map, max: number, count: number): void {
    if (count > max) {
      return;
    } else {
      const listener = map.addListener('zoom_changed', () => {
        listener.remove();
        this.smoothZoom(map, max, count + 1);
      });

      setTimeout(() => {
        map.setZoom(count);
      }, 80);
    }
  }

  private filterByRadius(markers: Array<Marker>, radius: number): Marker[] {
    const centerPos = this.centerPoint?.position as google.maps.LatLng | google.maps.LatLngLiteral;
    return markers.filter(({lat, lng}: Marker) => {
      const rawMarkerLatLng = new google.maps.LatLng({lat, lng});
      const d = google.maps.geometry.spherical.computeDistanceBetween(rawMarkerLatLng, centerPos);
      return d <= radius;
    });
  }

  @Watch('circleRadius')
  private onCircleRadiusChanged() {
    this.drawCircle(this.circleRadius || 0);
    this.createMarkers();
  }

  @Watch('center', {deep: true})
  private onCenterChanged() {
    this.map?.panTo(this.center as google.maps.LatLng | google.maps.LatLngLiteral);
    if (this.centerPoint) {
      this.centerPoint.position = this.map?.getCenter() ?? null;
    }
    this.circle?.setCenter(this.map?.getCenter() as google.maps.LatLng | google.maps.LatLngLiteral);
    this.createMarkers();
  }

}
